import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Input, Spinner } from '@mela/design-system';
import type { WaitlistSource } from '@mela/shared-types';
import { apiClient, isMockApi } from '../lib/client';
import { safeGet, safeSet } from '../lib/storage';

const RATE_LIMIT_KEY = 'mela.waitlist.lastSubmitAt';
const RATE_LIMIT_WINDOW_MS = 60_000; // one submit per minute per device — first line of anti-abuse.

const schema = z.object({
  email: z.string().trim().toLowerCase().email('Enter a valid work email'),
  full_name: z
    .string()
    .trim()
    .min(2, 'Too short')
    .max(80, 'Too long')
    .optional()
    .or(z.literal('')),
  city: z.string().trim().min(2).max(60).optional().or(z.literal('')),
  consent: z.literal(true, {
    errorMap: () => ({ message: 'Required to continue under DPDP' }),
  }),
  // Honeypot — invisible field. Real users leave it empty; dumb bots fill every input.
  // Server-side rate limiting (per IP, sha256-hashed) lives in the waitlist
  // edge function and catches scripts that POST directly.
  website_url: z.string().max(0, 'no thanks').optional().or(z.literal('')),
});

type FormValues = z.infer<typeof schema>;

interface Props {
  source?: WaitlistSource;
}

export function WaitlistForm({ source = 'marketing_site' }: Props) {
  const [success, setSuccess] = useState<{ reference_code: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema), mode: 'onBlur' });

  const onSubmit = async (data: FormValues) => {
    setError(null);

    // Honeypot tripped — bot. Pretend success so it doesn't retry.
    if (data.website_url && data.website_url.length > 0) {
      setSuccess({ reference_code: 'MELA-SILENT' });
      return;
    }

    // Client-side rate-limit: stops accidental double-submits and casual scripts.
    const last = Number(safeGet(RATE_LIMIT_KEY) ?? 0);
    if (Date.now() - last < RATE_LIMIT_WINDOW_MS) {
      setError('Please wait a minute before trying again.');
      return;
    }

    try {
      const res = await apiClient.joinWaitlist({
        email: data.email,
        full_name: data.full_name || undefined,
        city: data.city || undefined,
        source,
        consented_at: new Date().toISOString(),
      });
      safeSet(RATE_LIMIT_KEY, String(Date.now()));
      setSuccess({ reference_code: res.reference_code });
    } catch (e) {
      const code = (e as { code?: string })?.code;
      if (code === 'RATE_LIMITED') {
        setError('Too many requests from this network. Try again in a minute.');
      } else {
        setError('Could not join right now. Try again in a few minutes.');
      }
    }
  };

  if (success) {
    return (
      <div
        className="bg-green-50 border border-green-500 rounded-md p-6 text-center animate-fade-up"
        role="status"
        aria-live="polite"
      >
        <div className="font-display text-[20px] font-bold text-green-700 mb-2">You’re in.</div>
        <div className="text-[14px] text-ink-700 mb-3">
          Reference: <strong className="font-mono">{success.reference_code}</strong>
        </div>
        <div className="text-[13px] text-ink-500">
          We’ll send a confirmation link to your email shortly. Click it to lock your spot.
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3" noValidate>
      <div>
        <label htmlFor="email" className="sr-only">
          Work email
        </label>
        <Input
          id="email"
          type="email"
          placeholder="you@company.com"
          autoComplete="email"
          inputMode="email"
          {...register('email')}
          error={!!errors.email}
          disabled={isSubmitting}
          aria-invalid={!!errors.email}
        />
        {errors.email && (
          <p className="text-[12px] text-vermilion-700 mt-1">{errors.email.message}</p>
        )}
      </div>

      <div>
        <label htmlFor="full_name" className="sr-only">
          Your name
        </label>
        <Input
          id="full_name"
          type="text"
          placeholder="Your name (optional)"
          autoComplete="name"
          {...register('full_name')}
          disabled={isSubmitting}
        />
      </div>

      <div>
        <label htmlFor="city" className="sr-only">
          City
        </label>
        <Input
          id="city"
          type="text"
          placeholder="City (optional, e.g. Surat)"
          autoComplete="address-level2"
          {...register('city')}
          disabled={isSubmitting}
        />
      </div>

      {/* Honeypot — hidden from sighted users + screen readers. Bots fill it. */}
      <div aria-hidden="true" className="absolute left-[-9999px] w-px h-px overflow-hidden">
        <label htmlFor="website_url">Leave this field empty</label>
        <input
          id="website_url"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          {...register('website_url')}
        />
      </div>

      <label className="flex gap-2 items-start text-[12px] text-ink-600 leading-snug cursor-pointer">
        <input
          type="checkbox"
          {...register('consent')}
          className="mt-0.5 w-4 h-4 accent-saffron-500"
          disabled={isSubmitting}
        />
        <span>
          I agree to be contacted about Mela and accept the{' '}
          <a href="/about#privacy" className="underline decoration-dotted underline-offset-2 text-ink-800">
            Privacy Policy
          </a>{' '}
          (DPDP-compliant). You can withdraw consent any time.
        </span>
      </label>
      {errors.consent && (
        <p className="text-[12px] text-vermilion-700">{errors.consent.message}</p>
      )}

      <Button type="submit" variant="primary" size="lg" disabled={isSubmitting}>
        {isSubmitting ? (
          <>
            <Spinner size="md" /> Joining…
          </>
        ) : (
          'Claim my Mela ID'
        )}
      </Button>

      {error && (
        <p className="text-[13px] text-vermilion-700 text-center" role="alert">
          {error}
        </p>
      )}

      {isMockApi && (
        <p className="text-[11px] text-ink-400 text-center font-mono">
          dev mode · API not yet wired (Tab 1) — submissions return mock reference codes
        </p>
      )}
    </form>
  );
}
